import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import {
  sendAnalyticsData,
  useAppDispatch,
  useAppSelector,
  useFetchAPIs,
  formatRowsFluxoAtivos,
  getColumnsFluxoAtivos,
} from '@monorepo-web/logic-extrato';
import { Dropdown, Subtitulo, EnhancedTable, Line } from '@monorepo-web/components-library';

import HeaderWrapper from '../../MainLayout/HeaderWrapper';
import { TabelaFluxoAtivos } from '@monorepo-web/templates';

interface Props {
  ignoraMesAtual?: boolean | null;
  dispatchPDF?: boolean;
  sendData?: any;
}

export default function FluxoAtivos({ ignoraMesAtual, dispatchPDF, sendData }: Props) {
  const dispatch = useAppDispatch();
  const [
    obj_datas,
    fluxo_de_ativos,
    isLoaded,
    loading,
    loadedCarteiraList,
    isLoadingMinimized,
    params,
    preferenceCustomizacao,
  ] = useAppSelector((state) => [
    state.fluxoAtivos.obj_datas,
    state.fluxoAtivos.fluxo_de_ativos,
    state.fluxoAtivos.isLoaded,
    state.fluxoAtivos.loading,
    state.preference.loadedCarteiraList,
    state.loadMinimized.isLoadingMinimized,
    state.preference.params,
    state.preference.preferenceStyle.customizacao,
  ]);

  const opsDefinidas = preferenceCustomizacao?.FluxoAtivo_opsDefinidas ?? [];

  useEffect(() => {
    if (!dispatchPDF) document.title = `${(window as any).env.REACT_APP_TABNAME} - Fluxo de Ativos`;
  }, []);

  useEffect(() => {
    if (isLoaded && !dispatchPDF) dispatch(sendAnalyticsData(location.href));
  }, [params, isLoaded]);

  useFetchAPIs(isLoaded, loading, dispatchPDF, true);

  const [tiposAtv, setTiposAtv] = useState<(string | number)[]>([]);
  const [tiposOps, setTiposOps] = useState<(string | number)[]>([]);
  const [mudouFiltro, setMudouFiltro] = useState<boolean>(false);
  const [selectedTypes, setSelectedTypes] = useState<(string | number)[]>([]);

  const fluxoAtivosFiltered = useMemo(() => {
    if (fluxo_de_ativos && opsDefinidas?.length > 0) {
      return [
        fluxo_de_ativos[0],
        ...fluxo_de_ativos
          .slice(1)
          .filter(row => opsDefinidas.includes(row[3]?.toString()))
      ];
    }
    return fluxo_de_ativos || [];
  }, [fluxo_de_ativos, opsDefinidas]);

  useEffect(() => {
    const atualizaAtv =
      isLoaded && fluxoAtivosFiltered
        ? Array.from(new Set(fluxoAtivosFiltered.slice(1).map((row) => row[1])))
        : [];
    setTiposAtv(atualizaAtv);
    const atualizaOps =
      isLoaded && fluxoAtivosFiltered
        ? Array.from(new Set(fluxoAtivosFiltered.slice(1).map((row) => row[3])))
        : [];
    setTiposOps(atualizaOps);
    setMudouFiltro(false);
  }, [fluxoAtivosFiltered, isLoaded]);

  useEffect(() => {
    setSelectedTypes([...tiposAtv, ...tiposOps]);
  }, [tiposAtv, tiposOps]);

  const rowsFluxoAtivos = isLoaded
    ? formatRowsFluxoAtivos(fluxoAtivosFiltered, dispatchPDF, selectedTypes)
    : null;
  const columnsFluxoAtivos = getColumnsFluxoAtivos();

  const onSelect = (item: (string | number)[]) => {
    setSelectedTypes(item);
    setMudouFiltro(true);
  };
  const optionNameLabel = (item: string | number) => {
    return String(item);
  };

  const DropdownIcon = (props: any) => (
    <FontAwesomeIcon
      icon={faFilter as IconProp}
      style={{
        color: props.color,
        cursor: props.cursor,
      }}
      fontSize={'0.8rem'}
    />
  );

  const cachedFiltro = useCallback(
    () => (
      <Dropdown
        title="Filtros"
        selected={selectedTypes}
        options={[...tiposAtv, ...tiposOps]}
        onSelect={onSelect}
        optionNameLabel={optionNameLabel}
        secondColumnSeparator={tiposAtv.length}
        column1Title="Tipo Ativo"
        column2Title="Tipo Operação"
        botoesMarcarDesmarcar
        IconComponent={DropdownIcon}
        disabled={isLoadingMinimized}
      />
    ),
    [tiposAtv, tiposOps, selectedTypes, isLoadingMinimized],
  );

  useEffect(() => {
    if (dispatchPDF) {
      const data = {
        rowsFluxoAtivos,
        columnsFluxoAtivos,
      };
      sendData('FluxoAtivo', data);
    }
  }, []);

  if (dispatchPDF) {
    return <></>;
  }

  return (
    <>
      <Grid>
        <HeaderWrapper
          title="Fluxo de Ativos"
          periodo
          obj_datas={obj_datas}
          subtitle_date
          containerAux
          filtroTipo={cachedFiltro}
        />
        <Line />
      </Grid>
      <Grid container spacing={3}>
        {isLoaded ? (
          <Grid item xs={12}>
            <Subtitulo mb="8px">Movimentações</Subtitulo>
            {isLoaded && fluxoAtivosFiltered && fluxoAtivosFiltered.length > 1 ? (
              rowsFluxoAtivos && rowsFluxoAtivos.length == 0 && mudouFiltro ? (
                <span>Nenhuma movimentação encontrada para os filtros selecionados</span>
              ) : (
                <TabelaFluxoAtivos rows={rowsFluxoAtivos} titulo=''/>
              )
            ) : (
              <span>Nenhuma movimentação encontrada neste período</span>
            )}
          </Grid>
        ) : null}
      </Grid>
    </>
  );
}
